import React, { useState, useMemo, useEffect, useCallback } from "react"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Pagination,
  PaginationItem,
  PaginationLink,
  Spinner,
  Alert,
  InputGroup,
  InputGroupText,
} from "reactstrap"
import classnames from "classnames"
import { useHistory } from "react-router-dom"

// APIs
import {
  obtenerEstadisticasOnboarding,
  obtenerProgresosPendientes,
  descartarProgresoOnboarding,
} from "api/onboardingProgresoApi"

import { obtenerDashboard } from "api/funnelApi"

// Configuración
const ITEMS_POR_PAGINA = 20
const MAX_ALERTAS = 500

const NOMBRES_PASOS_ONBOARDING = [
  "Identificación",
  "Documentación",
  "KYC / AML",
  "Evaluación",
  "Firmantes",
  "Garantías",
  "Aprobación",
]

const TIPOS_CONTROL = [
  {
    id: "kyc_rescreening",
    nombre: "Re-screening KYC/AML",
    categoria: "compliance",
    descripcion: "Verificación periódica en listas restrictivas",
    frecuencias: { BAJO: 180, MEDIO: 90, ALTO: 30 },
  },
  {
    id: "bcra_check",
    nombre: "Consulta Central BCRA",
    categoria: "financiero",
    descripcion: "Verificar situación crediticia actual",
    frecuencias: { BAJO: 90, MEDIO: 30, ALTO: 15 },
  },
  {
    id: "repet_check",
    nombre: "Consulta RePET",
    categoria: "compliance",
    descripcion:
      "Consulta en Registro Público de Personas vinculadas al Terrorismo",
    frecuencias: { BAJO: 180, MEDIO: 90, ALTO: 30 },
  },
  {
    id: "scoring_recalc",
    nombre: "Recálculo Scoring",
    categoria: "financiero",
    descripcion: "Actualizar score crediticio",
    frecuencias: { BAJO: 180, MEDIO: 90, ALTO: 60 },
  },
  {
    id: "eeff_update",
    nombre: "Actualización EEFF",
    categoria: "financiero",
    descripcion: "Solicitar estados financieros actualizados",
    frecuencias: { BAJO: 365, MEDIO: 365, ALTO: 180 },
  },
  {
    id: "covenants_check",
    nombre: "Verificación Covenants",
    categoria: "financiero",
    descripcion: "Verificar cumplimiento de ratios",
    frecuencias: { BAJO: 180, MEDIO: 90, ALTO: 90 },
  },
  {
    id: "poderes_vigencia",
    nombre: "Vigencia de Poderes",
    categoria: "legal",
    descripcion: "Verificar vigencia de poderes",
    frecuencias: { BAJO: 365, MEDIO: 365, ALTO: 365 },
  },
  {
    id: "avaluo_garantia",
    nombre: "Avalúo de Garantías",
    categoria: "garantias",
    descripcion: "Revalúo de garantías",
    frecuencias: { BAJO: 730, MEDIO: 365, ALTO: 365 },
  },
]

const ESTADOS_CONTROL = {
  vigente: { label: "Vigente", color: "success" },
  proximo: { label: "Próximo (30d)", color: "info" },
  por_vencer: { label: "Por vencer (15d)", color: "warning" },
  vencido: { label: "Vencido", color: "danger" },
  alerta: { label: "Alerta", color: "danger" },
}

const RESULTADOS_CONTROL = {
  ok: { label: "OK", color: "success" },
  observado: { label: "Observado", color: "warning" },
  deterioro: { label: "Deterioro", color: "danger" },
}

// ============================================
// Componentes auxiliares
// ============================================

const MetricCard = ({
  titulo,
  valor,
  subtitulo,
  color = "primary",
  icon,
  onClick,
  active,
}) => (
  <Card
    className={`metric-card metric-${color} h-100 ${onClick ? "clickable" : ""} ${active ? "active" : ""}`}
    onClick={onClick}
  >
    <CardBody className="text-center py-4 text-muted">
      <div className="metric-icon">
        <i className={`bx ${icon}`} />
      </div>
      <div className={`metric-value text-${color}`}>{valor}</div>
      <div className="metric-title">{titulo}</div>
      {subtitulo && <div className="metric-subtitle">{subtitulo}</div>}
    </CardBody>
  </Card>
)

const ControlRow = ({
  control,
  onRegistrar,
  onVerHistorial,
  onVerDetalle,
  showEmpresa = true,
}) => {
  const estadoConfig = ESTADOS_CONTROL[control.estado]
  const resultadoConfig = control.ultimoResultado
    ? RESULTADOS_CONTROL[control.ultimoResultado]
    : null

  return (
    <tr
      className={
        control.estado === "vencido" || control.estado === "alerta"
          ? "table-danger"
          : ""
      }
    >
      <td>
        <strong>{control.nombre}</strong>
        <br />
        <small className="text-muted">{control.categoria}</small>
        {showEmpresa && control.empresaNombre && (
          <>
            <br />
            <small className="text-primary">{control.empresaNombre}</small>
          </>
        )}
      </td>
      <td>
        <Badge color={estadoConfig.color}>{estadoConfig.label}</Badge>
      </td>
      <td>{control.ultimaEjecucion || "-"}</td>
      <td>
        <span
          className={control.diasParaVencer <= 15 ? "text-danger fw-bold" : ""}
        >
          {control.proximaFecha}
        </span>
        <br />
        <small
          className={control.diasParaVencer <= 0 ? "text-danger" : "text-muted"}
        >
          {control.diasParaVencer <= 0
            ? `Vencido hace ${Math.abs(control.diasParaVencer)} días`
            : `En ${control.diasParaVencer} días`}
        </small>
      </td>
      <td>
        {resultadoConfig ? (
          <Badge color={resultadoConfig.color}>{resultadoConfig.label}</Badge>
        ) : (
          "-"
        )}
      </td>
      <td>
        <Button
          color="primary"
          size="sm"
          className="me-1"
          onClick={() => onRegistrar(control)}
          title="Registrar"
        >
          <i className="bx bx-plus" />
        </Button>
        <Button
          color="info"
          size="sm"
          className="me-1"
          onClick={() => onVerHistorial(control)}
          title="Historial"
        >
          <i className="bx bx-history" />
        </Button>
        <Button
          color="secondary"
          size="sm"
          onClick={() => onVerDetalle(control)}
          title="Detalle"
        >
          <i className="bx bx-show" />
        </Button>
      </td>
    </tr>
  )
}

const PaginacionTabla = ({ paginaActual, totalPaginas, onCambiarPagina }) => {
  if (totalPaginas <= 1) return null
  const paginas = []
  for (
    let i = Math.max(1, paginaActual - 2);
    i <= Math.min(totalPaginas, paginaActual + 2);
    i++
  )
    paginas.push(i)

  return (
    <Pagination className="mb-0 justify-content-center">
      <PaginationItem disabled={paginaActual === 1}>
        <PaginationLink first onClick={() => onCambiarPagina(1)} />
      </PaginationItem>
      <PaginationItem disabled={paginaActual === 1}>
        <PaginationLink
          previous
          onClick={() => onCambiarPagina(paginaActual - 1)}
        />
      </PaginationItem>
      {paginas[0] > 1 && (
        <PaginationItem disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      )}
      {paginas.map(num => (
        <PaginationItem key={num} active={num === paginaActual}>
          <PaginationLink onClick={() => onCambiarPagina(num)}>
            {num}
          </PaginationLink>
        </PaginationItem>
      ))}
      {paginas[paginas.length - 1] < totalPaginas && (
        <PaginationItem disabled>
          <PaginationLink>...</PaginationLink>
        </PaginationItem>
      )}
      <PaginationItem disabled={paginaActual === totalPaginas}>
        <PaginationLink
          next
          onClick={() => onCambiarPagina(paginaActual + 1)}
        />
      </PaginationItem>
      <PaginationItem disabled={paginaActual === totalPaginas}>
        <PaginationLink last onClick={() => onCambiarPagina(totalPaginas)} />
      </PaginationItem>
    </Pagination>
  )
}

const TiempoTranscurrido = ({ fecha }) => {
  if (!fecha) return <span className="text-muted">-</span>
  const diffMs = new Date() - new Date(fecha)
  const diffMins = Math.floor(diffMs / 60000)
  const diffHoras = Math.floor(diffMins / 60)
  const diffDias = Math.floor(diffHoras / 24)

  if (diffDias > 7) return <span className="text-danger">{diffDias} días</span>
  if (diffDias > 0)
    return (
      <span className="text-warning">
        {diffDias} día{diffDias > 1 ? "s" : ""}
      </span>
    )
  if (diffHoras > 0)
    return (
      <span className="text-muted">
        {diffHoras} hora{diffHoras > 1 ? "s" : ""}
      </span>
    )
  if (diffMins > 0) return <span className="text-muted">{diffMins} min</span>
  return <span className="text-muted">Ahora</span>
}

// ============================================
// Modales
// ============================================

const ModalRegistrarControl = ({ isOpen, toggle, control, onGuardar }) => {
  const [resultado, setResultado] = useState("")
  const [observacion, setObservacion] = useState("")
  const [guardando, setGuardando] = useState(false)
  const tipoControl = TIPOS_CONTROL.find(t => t.id === control?.tipoControlId)

  const handleGuardar = () => {
    setGuardando(true)
    setTimeout(() => {
      onGuardar({
        controlId: control?.id,
        resultado,
        observacion,
        fecha: new Date().toISOString(),
        fechaFormateada: new Date().toLocaleDateString("es-AR"),
        ejecutor: "Usuario Actual",
      })
      setResultado("")
      setObservacion("")
      setGuardando(false)
      toggle()
    }, 500)
  }

  const handleClose = () => {
    setResultado("")
    setObservacion("")
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Registrar: {control?.nombre}
      </ModalHeader>
      <ModalBody>
        {tipoControl && (
          <div className="alert alert-info py-2 mb-3">
            <small>{tipoControl.descripcion}</small>
          </div>
        )}
        {control?.empresaNombre && (
          <FormGroup>
            <Label>Empresa</Label>
            <Input type="text" value={control.empresaNombre} disabled />
          </FormGroup>
        )}
        <FormGroup>
          <Label>Resultado *</Label>
          <div className="d-flex gap-2">
            {Object.entries(RESULTADOS_CONTROL).map(([key, config]) => (
              <Button
                key={key}
                color={
                  resultado === key ? config.color : `outline-${config.color}`
                }
                onClick={() => setResultado(key)}
                className="flex-grow-1"
                size="sm"
              >
                {config.label}
              </Button>
            ))}
          </div>
        </FormGroup>
        <FormGroup>
          <Label>Observaciones</Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Detalle del control realizado..."
            value={observacion}
            onChange={e => setObservacion(e.target.value)}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleGuardar}
          disabled={!resultado || guardando}
        >
          {guardando ? (
            <>
              <i className="bx bx-loader-alt bx-spin me-1" />
              Guardando...
            </>
          ) : (
            "Guardar"
          )}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const ModalHistorial = ({ isOpen, toggle, control }) => {
  const historial = control?.historial || []
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Historial: {control?.nombre}</ModalHeader>
      <ModalBody>
        {control?.empresaNombre && (
          <p>
            <strong>Empresa:</strong> {control.empresaNombre}
          </p>
        )}
        {historial.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="bx bx-history" style={{ fontSize: 48 }} />
            <p className="mt-2">Sin registros anteriores</p>
          </div>
        ) : (
          <Table responsive size="sm">
            <thead className="table-light">
              <tr>
                <th>Fecha</th>
                <th>Resultado</th>
                <th>Ejecutor</th>
                <th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {historial.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.fechaFormateada}</td>
                  <td>
                    <Badge color={RESULTADOS_CONTROL[item.resultado]?.color}>
                      {RESULTADOS_CONTROL[item.resultado]?.label}
                    </Badge>
                  </td>
                  <td>{item.ejecutor}</td>
                  <td>
                    <small>{item.observacion || "-"}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const ModalDetalle = ({ isOpen, toggle, control }) => {
  const tipoControl = TIPOS_CONTROL.find(t => t.id === control?.tipoControlId)
  const estadoConfig = ESTADOS_CONTROL[control?.estado]
  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Detalle del Control</ModalHeader>
      <ModalBody>
        <table className="table table-sm table-borderless">
          <tbody>
            <tr>
              <td className="text-muted">Control:</td>
              <td>
                <strong>{control?.nombre}</strong>
              </td>
            </tr>
            {control?.empresaNombre && (
              <tr>
                <td className="text-muted">Empresa:</td>
                <td>{control.empresaNombre}</td>
              </tr>
            )}
            <tr>
              <td className="text-muted">Descripción:</td>
              <td>
                <small>{tipoControl?.descripcion}</small>
              </td>
            </tr>
            <tr>
              <td className="text-muted">Frecuencia:</td>
              <td>
                {tipoControl?.frecuencias?.[control?.nivelRiesgo]} días (
                {control?.nivelRiesgo})
              </td>
            </tr>
            <tr>
              <td className="text-muted">Estado:</td>
              <td>
                <Badge color={estadoConfig?.color}>{estadoConfig?.label}</Badge>
              </td>
            </tr>
            <tr>
              <td className="text-muted">Última Ejecución:</td>
              <td>{control?.ultimaEjecucion || "Nunca"}</td>
            </tr>
            <tr>
              <td className="text-muted">Próximo Vencimiento:</td>
              <td>{control?.proximaFecha}</td>
            </tr>
          </tbody>
        </table>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Cerrar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const ModalOnboardingDetalle = ({
  isOpen,
  toggle,
  progreso,
  onContinuar,
  onDescartar,
}) => {
  const [accionando, setAccionando] = useState(false)
  if (!progreso) return null

  const handleDescartar = async () => {
    setAccionando(true)
    try {
      await onDescartar(progreso.cuit)
      toggle()
    } finally {
      setAccionando(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Detalle del Onboarding</ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted">CUIT</small>
            <div>
              <code className="fs-5">{progreso.cuit}</code>
            </div>
          </Col>
          <Col md={6}>
            <small className="text-muted">Estado</small>
            <div>
              <Badge color="warning">{progreso.estado}</Badge>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted">Empresa</small>
            <div>{progreso.datosFormulario?.titular?.razonSocial || "-"}</div>
          </Col>
          <Col md={6}>
            <small className="text-muted">Paso Actual</small>
            <div>
              {progreso.pasoActual + 1}.{" "}
              {progreso.nombrePaso ||
                NOMBRES_PASOS_ONBOARDING[progreso.pasoActual]}
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md={6}>
            <small className="text-muted">Creado</small>
            <div>
              {progreso.fechaCreacion
                ? new Date(progreso.fechaCreacion).toLocaleString("es-AR")
                : "-"}
            </div>
          </Col>
          <Col md={6}>
            <small className="text-muted">Última modificación</small>
            <div>
              {progreso.fechaModificacion
                ? new Date(progreso.fechaModificacion).toLocaleString("es-AR")
                : "-"}
            </div>
          </Col>
        </Row>
        <div className="mt-4">
          <small className="text-muted d-block mb-2">Progreso</small>
          <div className="d-flex gap-1">
            {NOMBRES_PASOS_ONBOARDING.map((paso, idx) => (
              <div
                key={idx}
                title={paso}
                className={`flex-fill text-center py-2 px-1 rounded small ${idx < progreso.pasoActual ? "bg-success text-white" : idx === progreso.pasoActual ? "bg-warning text-white" : "bg-light text-muted"}`}
              >
                {idx + 1}
              </div>
            ))}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="danger"
          outline
          onClick={handleDescartar}
          disabled={accionando}
        >
          {accionando ? (
            <Spinner size="sm" />
          ) : (
            <i className="bx bx-trash me-1" />
          )}
          Descartar
        </Button>
        <Button color="primary" onClick={() => onContinuar(progreso)}>
          <i className="bx bx-play me-1" />
          Continuar
        </Button>
      </ModalFooter>
    </Modal>
  )
}

// ============================================
// Tab: Alertas
// ============================================

const TabAlertas = ({
  controles,
  onRegistrar,
  onVerHistorial,
  onVerDetalle,
}) => {
  const [filtroEstado, setFiltroEstado] = useState("todos")
  const [filtroCategoria, setFiltroCategoria] = useState("todos")
  const [paginaActual, setPaginaActual] = useState(1)

  const alertas = useMemo(() => {
    return controles
      .filter(
        c =>
          ["vencido", "por_vencer", "proximo", "alerta"].includes(c.estado) ||
          c.ultimoResultado === "deterioro",
      )
      .sort((a, b) => a.diasParaVencer - b.diasParaVencer)
      .slice(0, MAX_ALERTAS)
  }, [controles])

  const alertasFiltradas = useMemo(() => {
    return alertas.filter(c => {
      if (filtroEstado !== "todos" && c.estado !== filtroEstado) return false
      if (filtroCategoria !== "todos" && c.categoria !== filtroCategoria)
        return false
      return true
    })
  }, [alertas, filtroEstado, filtroCategoria])

  const totalPaginas = Math.ceil(alertasFiltradas.length / ITEMS_POR_PAGINA)
  const alertasPaginadas = alertasFiltradas.slice(
    (paginaActual - 1) * ITEMS_POR_PAGINA,
    paginaActual * ITEMS_POR_PAGINA,
  )

  const vencidos = alertas.filter(c => c.estado === "vencido").length
  const porVencer = alertas.filter(c => c.estado === "por_vencer").length
  const proximos = alertas.filter(c => c.estado === "proximo").length
  const deteriorados = alertas.filter(
    c => c.ultimoResultado === "deterioro",
  ).length

  return (
    <>
      <Row className="mb-4">
        <Col md={3}>
          <MetricCard
            titulo="Vencidos"
            valor={vencidos}
            icon="bx-x-circle"
            color="danger"
            onClick={() => {
              setFiltroEstado(filtroEstado === "vencido" ? "todos" : "vencido")
              setPaginaActual(1)
            }}
            active={filtroEstado === "vencido"}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="Por vencer (15d)"
            valor={porVencer}
            icon="bx-error"
            color="warning"
            onClick={() => {
              setFiltroEstado(
                filtroEstado === "por_vencer" ? "todos" : "por_vencer",
              )
              setPaginaActual(1)
            }}
            active={filtroEstado === "por_vencer"}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="Próximos (30d)"
            valor={proximos}
            icon="bx-time"
            color="info"
            onClick={() => {
              setFiltroEstado(filtroEstado === "proximo" ? "todos" : "proximo")
              setPaginaActual(1)
            }}
            active={filtroEstado === "proximo"}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="Con deterioro"
            valor={deteriorados}
            icon="bx-trending-down"
            color="danger"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Input
            type="select"
            value={filtroCategoria}
            onChange={e => {
              setFiltroCategoria(e.target.value)
              setPaginaActual(1)
            }}
          >
            <option value="todos">Todas las categorías</option>
            <option value="compliance">Compliance</option>
            <option value="financiero">Financiero</option>
            <option value="legal">Legal</option>
            <option value="garantias">Garantías</option>
          </Input>
        </Col>
        <Col md={8} className="text-end">
          <small className="text-muted">
            Mostrando {alertasFiltradas.length} alertas
          </small>
        </Col>
      </Row>

      <Card>
        <CardBody className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Control / Empresa</th>
                <th>Estado</th>
                <th>Última Ejec.</th>
                <th>Vencimiento</th>
                <th>Resultado</th>
                <th style={{ width: 130 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {alertasPaginadas.length > 0 ? (
                alertasPaginadas.map(control => (
                  <ControlRow
                    key={control.id}
                    control={control}
                    onRegistrar={onRegistrar}
                    onVerHistorial={onVerHistorial}
                    onVerDetalle={onVerDetalle}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    <i
                      className="bx bx-check-circle text-success"
                      style={{ fontSize: 32 }}
                    />
                    <p className="mb-0 mt-2">No hay alertas pendientes</p>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
        {totalPaginas > 1 && (
          <CardBody className="border-top py-2">
            <PaginacionTabla
              paginaActual={paginaActual}
              totalPaginas={totalPaginas}
              onCambiarPagina={setPaginaActual}
            />
          </CardBody>
        )}
      </Card>
    </>
  )
}

// ============================================
// Tab: Por Empresa
// ============================================

const TabPorEmpresa = ({
  empresas,
  controles,
  onRegistrar,
  onVerHistorial,
  onVerDetalle,
}) => {
  const [busqueda, setBusqueda] = useState("")
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null)

  const empresasFiltradas = useMemo(() => {
    if (!busqueda) return empresas.slice(0, 50)
    const termino = busqueda.toLowerCase()
    return empresas
      .filter(
        e =>
          e.razonSocial?.toLowerCase().includes(termino) ||
          e.cuit?.includes(termino),
      )
      .slice(0, 50)
  }, [empresas, busqueda])

  const controlesEmpresa = useMemo(() => {
    if (!empresaSeleccionada) return []
    return controles.filter(c => c.empresaId === empresaSeleccionada.id)
  }, [controles, empresaSeleccionada])

  const metricasEmpresa = useMemo(() => {
    if (!controlesEmpresa.length) return null
    return {
      total: controlesEmpresa.length,
      vigentes: controlesEmpresa.filter(c => c.estado === "vigente").length,
      alertas: controlesEmpresa.filter(c =>
        ["vencido", "por_vencer"].includes(c.estado),
      ).length,
    }
  }, [controlesEmpresa])

  return (
    <Row>
      <Col md={4}>
        <Card>
          <CardHeader className="py-2 text-muted">
            <strong>Buscar Empresa</strong>
          </CardHeader>
          <CardBody className="p-2 text-muted">
            <Input
              type="text"
              placeholder="Buscar por razón social o CUIT..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="mb-2"
            />
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              {empresasFiltradas.length > 0 ? (
                empresasFiltradas.map(empresa => {
                  const alertasEmpresa = controles.filter(
                    c =>
                      c.empresaId === empresa.id &&
                      ["vencido", "por_vencer"].includes(c.estado),
                  ).length
                  return (
                    <div
                      key={empresa.id}
                      className={`p-2 border-bottom ${empresaSeleccionada?.id === empresa.id ? "bg-primary bg-opacity-10" : ""}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setEmpresaSeleccionada(empresa)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong className="d-block">
                            {empresa.razonSocial}
                          </strong>
                          <small className="text-muted">{empresa.cuit}</small>
                        </div>
                        {alertasEmpresa > 0 && (
                          <Badge color="danger" pill>
                            {alertasEmpresa}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )
                })
              ) : (
                <p className="text-muted text-center py-3 mb-0">
                  {busqueda
                    ? "No se encontraron empresas"
                    : "Ingrese un término de búsqueda"}
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col md={8}>
        {empresaSeleccionada ? (
          <>
            <Card className="mb-3">
              <CardBody className="py-2">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="mb-0">{empresaSeleccionada.razonSocial}</h5>
                    <small className="text-muted">
                      CUIT: {empresaSeleccionada.cuit}
                    </small>
                  </Col>
                  {metricasEmpresa && (
                    <Col xs="auto" className="text-end">
                      <Badge color="success" className="me-2">
                        {metricasEmpresa.vigentes} vigentes
                      </Badge>
                      {metricasEmpresa.alertas > 0 && (
                        <Badge color="danger">
                          {metricasEmpresa.alertas} alertas
                        </Badge>
                      )}
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>
            <Card>
              <CardBody className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Control</th>
                      <th>Estado</th>
                      <th>Última Ejec.</th>
                      <th>Vencimiento</th>
                      <th>Resultado</th>
                      <th style={{ width: 130 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {controlesEmpresa.map(control => (
                      <ControlRow
                        key={control.id}
                        control={control}
                        onRegistrar={onRegistrar}
                        onVerHistorial={onVerHistorial}
                        onVerDetalle={onVerDetalle}
                        showEmpresa={false}
                      />
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </>
        ) : (
          <Card>
            <CardBody className="text-center py-5 text-muted">
              <i className="bx bx-building" style={{ fontSize: 48 }} />
              <p className="mt-2 mb-0">
                Seleccione una empresa para ver sus controles
              </p>
            </CardBody>
          </Card>
        )}
      </Col>
    </Row>
  )
}

// ============================================
// Tab: Onboardings Pendientes
// ============================================

const TabOnboardings = () => {
  const history = useHistory()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [estadisticas, setEstadisticas] = useState(null)
  const [progresos, setProgresos] = useState([])
  const [filtroTexto, setFiltroTexto] = useState("")
  const [filtroPaso, setFiltroPaso] = useState("")
  const [modalDetalle, setModalDetalle] = useState({
    open: false,
    progreso: null,
  })

  const cargarDatos = useCallback(async () => {
    setLoading(true)
    try {
      const [stats, pendientes] = await Promise.all([
        obtenerEstadisticasOnboarding(),
        obtenerProgresosPendientes(),
      ])
      setEstadisticas(stats)
      setProgresos(pendientes || [])
      setError(null)
    } catch (err) {
      console.error("Error cargando onboardings:", err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargarDatos()
    const interval = setInterval(cargarDatos, 60000)
    return () => clearInterval(interval)
  }, [cargarDatos])

  const progresosFiltrados = useMemo(() => {
    return progresos.filter(p => {
      const matchTexto =
        !filtroTexto ||
        p.cuit?.includes(filtroTexto) ||
        p.datosFormulario?.titular?.razonSocial
          ?.toLowerCase()
          .includes(filtroTexto.toLowerCase())
      const matchPaso = !filtroPaso || p.pasoActual === parseInt(filtroPaso)
      return matchTexto && matchPaso
    })
  }, [progresos, filtroTexto, filtroPaso])

  const handleContinuar = progreso =>
    history.push(`/onboarding?cuit=${progreso.cuit}`)
  const handleDescartar = async cuit => {
    try {
      await descartarProgresoOnboarding(cuit)
      cargarDatos()
    } catch (err) {
      setError(err.message)
    }
  }

  if (loading && progresos.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">Cargando onboardings...</p>
      </div>
    )
  }

  return (
    <>
      {error && (
        <Alert color="danger" className="mb-3">
          <i className="bx bx-error me-2" />
          {error}
        </Alert>
      )}

      {estadisticas && (
        <Row className="mb-4">
          <Col md={3}>
            <MetricCard
              titulo="En Progreso"
              valor={estadisticas.totalEnProgreso || 0}
              icon="bx-time"
              color="warning"
            />
          </Col>
          <Col md={3}>
            <MetricCard
              titulo="Completados"
              valor={estadisticas.totalCompletados || 0}
              icon="bx-check-circle"
              color="success"
            />
          </Col>
          <Col md={3}>
            <MetricCard
              titulo="Abandonados"
              valor={estadisticas.totalAbandonados || 0}
              icon="bx-x"
              color="secondary"
            />
          </Col>
          <Col md={3}>
            <MetricCard
              titulo="Tasa Conversión"
              valor={`${estadisticas.totalEnProgreso + estadisticas.totalCompletados > 0 ? Math.round((estadisticas.totalCompletados / (estadisticas.totalEnProgreso + estadisticas.totalCompletados + estadisticas.totalAbandonados)) * 100) : 0}%`}
              icon="bx-trending-up"
              color="primary"
            />
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={4}>
          <InputGroup>
            <InputGroupText>
              <i className="bx bx-search" />
            </InputGroupText>
            <Input
              placeholder="Buscar por CUIT o razón social..."
              value={filtroTexto}
              onChange={e => setFiltroTexto(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={3}>
          <Input
            type="select"
            value={filtroPaso}
            onChange={e => setFiltroPaso(e.target.value)}
          >
            <option value="">Todos los pasos</option>
            {NOMBRES_PASOS_ONBOARDING.map((paso, idx) => (
              <option key={idx} value={idx}>
                {idx + 1}. {paso}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={5} className="text-end">
          <Button
            color="light"
            size="sm"
            onClick={cargarDatos}
            disabled={loading}
          >
            <i className={`bx bx-refresh ${loading ? "bx-spin" : ""} me-1`} />
            Actualizar
          </Button>
        </Col>
      </Row>

      <Card>
        <CardBody className="p-0">
          {progresosFiltrados.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i
                className="bx bx-check-circle text-success"
                style={{ fontSize: 48 }}
              />
              <p className="mt-2 mb-0">No hay onboardings pendientes</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>CUIT</th>
                  <th>Empresa</th>
                  <th>Paso Actual</th>
                  <th>Última Actividad</th>
                  <th>Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {progresosFiltrados.map(progreso => (
                  <tr key={progreso.id}>
                    <td>
                      <code>{progreso.cuit}</code>
                    </td>
                    <td>
                      {progreso.datosFormulario?.titular?.razonSocial ||
                        progreso.datosFormulario?.titular?.nombre || (
                          <span className="text-muted">-</span>
                        )}
                    </td>
                    <td>
                      <Badge color="light" className="text-dark">
                        {progreso.pasoActual + 1}.{" "}
                        {progreso.nombrePaso ||
                          NOMBRES_PASOS_ONBOARDING[progreso.pasoActual]}
                      </Badge>
                    </td>
                    <td>
                      <TiempoTranscurrido fecha={progreso.fechaModificacion} />
                    </td>
                    <td>
                      <Badge color="warning">{progreso.estado}</Badge>
                    </td>
                    <td className="text-center">
                      <Button
                        color="primary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleContinuar(progreso)}
                        title="Continuar"
                      >
                        <i className="bx bx-play" />
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        onClick={() =>
                          setModalDetalle({ open: true, progreso })
                        }
                        title="Ver detalle"
                      >
                        <i className="bx bx-show" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <ModalOnboardingDetalle
        isOpen={modalDetalle.open}
        toggle={() => setModalDetalle({ open: false, progreso: null })}
        progreso={modalDetalle.progreso}
        onContinuar={handleContinuar}
        onDescartar={handleDescartar}
      />
    </>
  )
}

// ============================================
// Tab: Funnel
// ============================================

const TabFunnel = () => {
  const [loading, setLoading] = useState(true)
  const [dashboard, setDashboard] = useState(null)
  const [periodo, setPeriodo] = useState("semana") // hoy, semana, mes

  const cargarDatos = useCallback(async () => {
    setLoading(true)
    try {
      const data = await obtenerDashboard(periodo)
      setDashboard(data)
    } catch (err) {
      console.error("Error cargando funnel:", err)
    } finally {
      setLoading(false)
    }
  }, [periodo])

  useEffect(() => {
    cargarDatos()
  }, [cargarDatos])

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-2 text-muted">Cargando métricas...</p>
      </div>
    )
  }

  // Calcular métricas
  const tasaConversion =
    dashboard?.iniciadosHoy > 0
      ? Math.round((dashboard.completadosHoy / dashboard.iniciadosHoy) * 100)
      : 0

  const tasaAbandono =
    dashboard?.iniciadosHoy > 0
      ? Math.round((dashboard.abandonadosHoy / dashboard.iniciadosHoy) * 100)
      : 0

  const ultimosDias = dashboard?.ultimosSieteDias || []
  const metricasPorPaso = dashboard?.metricasPorPaso || []
  const cuellosBottella = dashboard?.cuellosBottella || []
  const tiemposPorPaso = dashboard?.tiemposPorPaso || []

  return (
    <>
      {/* Selector de período */}
      <Row className="mb-4">
        <Col md={4}>
          <Input
            type="select"
            value={periodo}
            onChange={e => setPeriodo(e.target.value)}
          >
            <option value="hoy">Hoy</option>
            <option value="semana">Última semana</option>
            <option value="mes">Último mes</option>
          </Input>
        </Col>
        <Col md={8} className="text-end">
          <Button
            color="light"
            size="sm"
            onClick={cargarDatos}
            disabled={loading}
          >
            <i className={`bx bx-refresh ${loading ? "bx-spin" : ""} me-1`} />
            Actualizar
          </Button>
        </Col>
      </Row>

      {/* Métricas principales */}
      <Row className="mb-4">
        <Col md={3}>
          <MetricCard
            titulo="Iniciados"
            valor={dashboard?.iniciadosHoy || 0}
            icon="bx-log-in"
            color="primary"
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="Completados"
            valor={dashboard?.completadosHoy || 0}
            icon="bx-check-circle"
            color="success"
            subtitulo={`${tasaConversion}% conversión`}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="Abandonados"
            valor={dashboard?.abandonadosHoy || 0}
            icon="bx-log-out"
            color="danger"
            subtitulo={`${tasaAbandono}% abandono`}
          />
        </Col>
        <Col md={3}>
          <MetricCard
            titulo="En Progreso"
            valor={dashboard?.enProgresoAhora || 0}
            icon="bx-time"
            color="warning"
          />
        </Col>
      </Row>

      {/* Alertas */}
      {dashboard?.alertas && dashboard.alertas.length > 0 && (
        <Alert color="warning" className="mb-4 text-muted">
          <strong>
            <i className="bx bx-error me-2" />
            Alertas del Sistema:
          </strong>
          <ul className="mb-0 mt-2">
            {dashboard.alertas.map((alerta, idx) => (
              <li key={idx}>
                <strong>{alerta.tipo}:</strong> {alerta.mensaje}
                <span className="text-muted ms-2">
                  (Valor: {alerta.valor}%, Umbral: {alerta.umbral}%)
                </span>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Row>
        {/* Columna izquierda: Funnel y tiempos */}
        <Col lg={8}>
          {/* Visualización del Funnel */}
          <Card className="mb-4  text-muted">
            <CardHeader className="bg-white d-flex justify-content-between align-items-center text-muted">
              <strong>
                <i className="bx bx-filter me-2" />
                Embudo de Conversión
              </strong>
            </CardHeader>
            <CardBody>
              {metricasPorPaso.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bx bx-bar-chart" style={{ fontSize: 48 }} />
                  <p className="mt-2 mb-0">Sin datos suficientes aún</p>
                </div>
              ) : (
                <div>
                  {metricasPorPaso.map((paso, idx) => {
                    const porcentajeDelTotal =
                      metricasPorPaso[0]?.llegaron > 0
                        ? Math.round(
                            (paso.llegaron / metricasPorPaso[0].llegaron) * 100,
                          )
                        : 0
                    const abandonoPaso =
                      paso.llegaron > 0
                        ? Math.round((paso.abandonaron / paso.llegaron) * 100)
                        : 0

                    return (
                      <div key={idx} className="mb-3 text-muted">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span>
                            <Badge color="light" className="text-dark me-2">
                              {idx + 1}
                            </Badge>
                            <strong>{NOMBRES_PASOS_ONBOARDING[idx]}</strong>
                          </span>
                          <span className="text-muted small">
                            {paso.llegaron} llegaron |
                            <span className="text-success">
                              {" "}
                              {paso.completaron} completaron
                            </span>{" "}
                            |
                            <span className="text-danger">
                              {" "}
                              {paso.abandonaron} abandonaron
                            </span>
                          </span>
                        </div>

                        {/* Barra del funnel */}
                        <div
                          className="position-relative  text-muted"
                          style={{ height: 32 }}
                        >
                          <div
                            className="position-absolute h-100 rounded"
                            style={{
                              width: `${porcentajeDelTotal}%`,
                              backgroundColor:
                                abandonoPaso > 30 ? "#ffc107" : "#28a745",
                              minWidth: 40,
                              transition: "width 0.5s ease",
                            }}
                          />
                          <div
                            className="position-absolute h-100 d-flex align-items-center px-2 text-muted"
                            style={{
                              color: porcentajeDelTotal > 20 ? "#fff" : "#333",
                            }}
                          >
                            <span className="small fw-bold">
                              {porcentajeDelTotal}%
                            </span>
                            {abandonoPaso > 20 && (
                              <span className="ms-2 small">
                                <i className="bx bx-error text-warning" />{" "}
                                {abandonoPaso}% abandono
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Tiempo por paso */}
          <Card className="mb-4 text-muted">
            <CardHeader className="bg-white text-muted">
              <strong>
                <i className="bx bx-time-five me-2" />
                Tiempo Promedio por Paso
              </strong>
            </CardHeader>
            <CardBody>
              {tiemposPorPaso.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bx bx-time" style={{ fontSize: 48 }} />
                  <p className="mt-2 mb-0">Sin datos de tiempo aún</p>
                </div>
              ) : (
                <Table responsive className="mb-0 text-muted">
                  <thead className="table-light">
                    <tr>
                      <th>Paso</th>
                      <th>Tiempo Promedio</th>
                      <th>Mínimo</th>
                      <th>Máximo</th>
                      <th>Evaluación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tiemposPorPaso.map((paso, idx) => {
                      const tiempoMin = Math.round(paso.tiempoPromedio / 60)
                      const esLento = tiempoMin > (paso.umbralLento || 10)
                      const esMuyLento = tiempoMin > (paso.umbralCritico || 20)

                      return (
                        <tr
                          key={idx}
                          className={
                            esMuyLento
                              ? "table-danger"
                              : esLento
                                ? "table-warning"
                                : ""
                          }
                        >
                          <td>
                            <Badge color="light" className="text-dark">
                              {idx + 1}. {NOMBRES_PASOS_ONBOARDING[idx]}
                            </Badge>
                          </td>
                          <td>
                            <strong>
                              {formatearTiempo(paso.tiempoPromedio)}
                            </strong>
                          </td>
                          <td className="text-muted">
                            {formatearTiempo(paso.tiempoMinimo)}
                          </td>
                          <td className="text-muted">
                            {formatearTiempo(paso.tiempoMaximo)}
                          </td>
                          <td>
                            {esMuyLento ? (
                              <Badge color="danger">
                                <i className="bx bx-error me-1" />
                                Crítico
                              </Badge>
                            ) : esLento ? (
                              <Badge color="warning">
                                <i className="bx bx-time me-1" />
                                Lento
                              </Badge>
                            ) : (
                              <Badge color="success">
                                <i className="bx bx-check me-1" />
                                OK
                              </Badge>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* Columna derecha: Insights */}
        <Col lg={4}>
          {/* Cuellos de botella */}
          <Card className="mb-4 text-muted">
            <CardHeader className="bg-white text-muted">
              <strong>
                <i className="bx bx-block me-2" />
                Cuellos de Botella
              </strong>
            </CardHeader>
            <CardBody>
              {cuellosBottella.length === 0 ? (
                <div className="text-center py-3 text-muted">
                  <i
                    className="bx bx-check-circle text-success"
                    style={{ fontSize: 32 }}
                  />
                  <p className="mt-2 mb-0 small">No se detectaron problemas</p>
                </div>
              ) : (
                <ul className="list-unstyled mb-0">
                  {cuellosBottella.map((cuello, idx) => (
                    <li
                      key={idx}
                      className="mb-3 p-2 border-start border-3 border-warning bg-light rounded-end"
                    >
                      <strong className="d-block">
                        {NOMBRES_PASOS_ONBOARDING[cuello.paso]}
                      </strong>
                      <small className="text-muted">{cuello.motivo}</small>
                      <div className="mt-1">
                        <Badge color="warning">
                          {cuello.valor}% {cuello.metrica}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Recomendaciones */}
          <Card className="mb-4 text-muted">
            <CardHeader className="bg-white text-muted">
              <strong>
                <i className="bx bx-bulb me-2" />
                Recomendaciones
              </strong>
            </CardHeader>
            <CardBody>
              {dashboard?.recomendaciones &&
              dashboard.recomendaciones.length > 0 ? (
                <ul className="list-unstyled mb-0">
                  {dashboard.recomendaciones.map((rec, idx) => (
                    <li key={idx} className="mb-2 d-flex">
                      <i
                        className={`bx ${rec.icono || "bx-right-arrow-alt"} text-primary me-2 mt-1`}
                      />
                      <span className="small">{rec.texto}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="list-unstyled mb-0 small">
                  <li className="mb-2 d-flex">
                    <i className="bx bx-right-arrow-alt text-primary me-2 mt-1" />
                    <span>Analizar pasos con alto abandono (&gt;30%)</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bx bx-right-arrow-alt text-primary me-2 mt-1" />
                    <span>Simplificar pasos que toman más de 10 minutos</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bx bx-right-arrow-alt text-primary me-2 mt-1" />
                    <span>Revisar errores frecuentes en validaciones</span>
                  </li>
                </ul>
              )}
            </CardBody>
          </Card>

          {/* Variación vs período anterior */}
          <Card className=" text-muted">
            <CardHeader className="bg-white text-muted">
              <strong>
                <i className="bx bx-trending-up me-2" />
                Variación vs Período Anterior
              </strong>
            </CardHeader>
            <CardBody>
              <div className="d-flex justify-content-between mb-2">
                <span>Iniciados</span>
                <VariacionBadge valor={dashboard?.variacionIniciados} />
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Conversión</span>
                <VariacionBadge valor={dashboard?.variacionConversion} />
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Abandono</span>
                <VariacionBadge
                  valor={dashboard?.variacionAbandono}
                  invertido
                />
              </div>
              <div className="d-flex justify-content-between">
                <span>Tiempo promedio</span>
                <VariacionBadge valor={dashboard?.variacionTiempo} invertido />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Tendencia histórica */}
      <Card className="mt-4 text-muted">
        <CardHeader className="bg-white text-muted">
          <strong>
            <i className="bx bx-line-chart me-2" />
            Tendencia Histórica
          </strong>
        </CardHeader>
        <CardBody>
          {ultimosDias.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bx bx-line-chart" style={{ fontSize: 48 }} />
              <p className="mt-2 mb-0">Sin datos históricos aún</p>
            </div>
          ) : (
            <Table responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Iniciados</th>
                  <th>Completados</th>
                  <th>Abandonados</th>
                  <th>Tasa Conversión</th>
                  <th>Tendencia</th>
                </tr>
              </thead>
              <tbody>
                {ultimosDias.map((dia, idx) => {
                  const fecha = dia.fecha
                    ? `${dia.fecha.dayOfMonth}/${dia.fecha.monthValue}`
                    : "-"
                  const conversion =
                    dia.tasaConversion ||
                    (dia.iniciados > 0
                      ? Math.round((dia.completados / dia.iniciados) * 100)
                      : 0)
                  const diaAnterior = ultimosDias[idx + 1]
                  const conversionAnterior = diaAnterior?.tasaConversion || 0
                  const tendencia = conversion - conversionAnterior

                  return (
                    <tr key={idx}>
                      <td>
                        <strong>{fecha}</strong>
                      </td>
                      <td>{dia.iniciados || 0}</td>
                      <td className="text-success">{dia.completados || 0}</td>
                      <td className="text-danger">{dia.abandonados || 0}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              width: 50,
                              height: 6,
                              backgroundColor: "#e9ecef",
                              borderRadius: 3,
                              marginRight: 8,
                            }}
                          >
                            <div
                              style={{
                                width: `${conversion}%`,
                                height: "100%",
                                backgroundColor:
                                  conversion >= 50
                                    ? "#28a745"
                                    : conversion >= 25
                                      ? "#ffc107"
                                      : "#dc3545",
                                borderRadius: 3,
                              }}
                            />
                          </div>
                          <span
                            className={
                              conversion >= 50
                                ? "text-success"
                                : conversion >= 25
                                  ? "text-warning"
                                  : "text-danger"
                            }
                          >
                            {conversion}%
                          </span>
                        </div>
                      </td>
                      <td>
                        {idx < ultimosDias.length - 1 &&
                          (tendencia > 0 ? (
                            <span className="text-success">
                              <i className="bx bx-up-arrow-alt" /> +{tendencia}%
                            </span>
                          ) : tendencia < 0 ? (
                            <span className="text-danger">
                              <i className="bx bx-down-arrow-alt" /> {tendencia}
                              %
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          ))}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </>
  )
}

// Componente auxiliar para mostrar variación
const VariacionBadge = ({ valor, invertido = false }) => {
  if (valor === undefined || valor === null) {
    return <span className="text-muted">—</span>
  }

  const esPositivo = invertido ? valor < 0 : valor > 0
  const esNegativo = invertido ? valor > 0 : valor < 0

  return (
    <span
      className={
        esPositivo ? "text-success" : esNegativo ? "text-danger" : "text-muted"
      }
    >
      {valor > 0 ? "+" : ""}
      {valor}%{esPositivo && <i className="bx bx-up-arrow-alt ms-1" />}
      {esNegativo && <i className="bx bx-down-arrow-alt ms-1" />}
    </span>
  )
}

// Función auxiliar para formatear tiempo
const formatearTiempo = segundos => {
  if (!segundos) return "-"

  if (segundos < 60) {
    return `${segundos}s`
  } else if (segundos < 3600) {
    const min = Math.floor(segundos / 60)
    const seg = segundos % 60
    return seg > 0 ? `${min}m ${seg}s` : `${min}m`
  } else {
    const hrs = Math.floor(segundos / 3600)
    const min = Math.floor((segundos % 3600) / 60)
    return `${hrs}h ${min}m`
  }
}

// ============================================
// Componente Principal
// ============================================

const MonitoreoPanel = ({ empresas = [], controles = [], setControles }) => {
  const [activeTab, setActiveTab] = useState("alertas")
  const [modalRegistrar, setModalRegistrar] = useState({
    open: false,
    control: null,
  })
  const [modalHistorial, setModalHistorial] = useState({
    open: false,
    control: null,
  })
  const [modalDetalle, setModalDetalle] = useState({
    open: false,
    control: null,
  })

  const handleRegistrar = control => setModalRegistrar({ open: true, control })
  const handleVerHistorial = control =>
    setModalHistorial({ open: true, control })
  const handleVerDetalle = control => setModalDetalle({ open: true, control })

  const handleGuardarRegistro = registro => {
    setControles(prev =>
      prev.map(c => {
        if (c.id === registro.controlId) {
          const tipoControl = TIPOS_CONTROL.find(t => t.id === c.tipoControlId)
          const frecuencia = tipoControl?.frecuencias[c.nivelRiesgo] || 90
          const nuevaFecha = new Date()
          nuevaFecha.setDate(nuevaFecha.getDate() + frecuencia)
          return {
            ...c,
            ultimaEjecucion: registro.fechaFormateada,
            ultimoResultado: registro.resultado,
            proximaFecha: nuevaFecha.toLocaleDateString("es-AR"),
            diasParaVencer: frecuencia,
            estado: "vigente",
            historial: [registro, ...(c.historial || [])],
          }
        }
        return c
      }),
    )
  }

  const totalAlertas = controles.filter(c =>
    ["vencido", "por_vencer"].includes(c.estado),
  ).length

  return (
    <div>
      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "alertas" })}
            onClick={() => setActiveTab("alertas")}
            style={{ cursor: "pointer" }}
          >
            <i className="bx bx-bell me-1" />
            Alertas
            {totalAlertas > 0 && (
              <Badge color="danger" pill className="ms-2">
                {totalAlertas}
              </Badge>
            )}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "empresa" })}
            onClick={() => setActiveTab("empresa")}
            style={{ cursor: "pointer" }}
          >
            <i className="bx bx-building me-1" />
            Por Empresa
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "onboardings" })}
            onClick={() => setActiveTab("onboardings")}
            style={{ cursor: "pointer" }}
          >
            <i className="bx bx-user-plus me-1" />
            Onboardings
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={classnames({ active: activeTab === "funnel" })}
            onClick={() => setActiveTab("funnel")}
            style={{ cursor: "pointer" }}
          >
            <i className="bx bx-filter me-1" />
            Funnel
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        <TabPane tabId="alertas">
          <TabAlertas
            controles={controles}
            onRegistrar={handleRegistrar}
            onVerHistorial={handleVerHistorial}
            onVerDetalle={handleVerDetalle}
          />
        </TabPane>
        <TabPane tabId="empresa">
          <TabPorEmpresa
            empresas={empresas}
            controles={controles}
            onRegistrar={handleRegistrar}
            onVerHistorial={handleVerHistorial}
            onVerDetalle={handleVerDetalle}
          />
        </TabPane>
        <TabPane tabId="onboardings">
          <TabOnboardings />
        </TabPane>
        <TabPane tabId="funnel">
          <TabFunnel />
        </TabPane>
      </TabContent>

      <ModalRegistrarControl
        isOpen={modalRegistrar.open}
        toggle={() => setModalRegistrar({ open: false, control: null })}
        control={modalRegistrar.control}
        onGuardar={handleGuardarRegistro}
      />
      <ModalHistorial
        isOpen={modalHistorial.open}
        toggle={() => setModalHistorial({ open: false, control: null })}
        control={modalHistorial.control}
      />
      <ModalDetalle
        isOpen={modalDetalle.open}
        toggle={() => setModalDetalle({ open: false, control: null })}
        control={modalDetalle.control}
      />
    </div>
  )
}

export default MonitoreoPanel
